import React, { ReactNode, useState } from 'react';
import {
  Autocomplete,
  AutocompleteProps,
  Box,
  ClickAwayListener,
  MenuItem,
  SvgIcon,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { Checkbox } from '@components/checkbox/Checkbox';
import { Chip } from '@components/chip';
import { Tooltip } from '@components/tooltip';
import { TextField } from '@components/textField';

export interface TypeAheadFilterProps {
  label?: string;
  disabled?: boolean;
  onChange: (value: any, reason?: string) => void;
  value?: any;
  options?: { label: string; value: string; subLabel?: string; sx?: SxProps<Theme>; icon?: ReactNode }[];
  isMultiple?: boolean;
  limitTags?: number;
  helperText?: string;
  onInputChange?: (event: any, value: string) => void;
  loading?: boolean;
  slotProps?: AutocompleteProps<any, boolean, boolean, boolean, any>['slotProps'];
  selectedItemDefaultLabel?: string;
  horizontalLabel?: boolean;
  freeSolo?: boolean;
  disableClearable?: boolean;
  defaultValue?: string | string[];
  hideTags?: boolean;
  hideCheckboxes?: boolean;
  highlightSearchText?: boolean;
  required?: boolean;
  error?: boolean;
  persistSearchOnSelect?: boolean;
}

export const TypeAheadFilter: React.FC<TypeAheadFilterProps> = ({
  required = false,
  value,
  disabled = false,
  label,
  options = [],
  isMultiple = false,
  limitTags = 1,
  onChange,
  helperText,
  onInputChange,
  loading = false,
  slotProps,
  selectedItemDefaultLabel,
  horizontalLabel,
  freeSolo,
  disableClearable,
  defaultValue,
  hideTags,
  hideCheckboxes,
  highlightSearchText,
  error,
  persistSearchOnSelect = true,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string | undefined>(undefined);

  const chipSxProps = {
    color: 'text.main',
    fontWeight: 'normal',
    fontSize: 'inherit',
    mr: '4px',
    alignSelf: 'center',
  };

  return (
    <ClickAwayListener
      touchEvent={false} // disables touch events
      onClickAway={() => setOpen(false)}
    >
      <Autocomplete
        open={open}
        freeSolo={freeSolo}
        disableClearable={isMultiple && persistSearchOnSelect ? !inputValue : disableClearable}
        onOpen={() => setOpen(true)}
        onClose={(event, reason) => {
          if (reason === 'toggleInput') {
            setOpen(false);
          }
          if ((reason === 'blur' || reason === 'escape') && isMultiple && persistSearchOnSelect) {
            setOpen(false);
            setInputValue(undefined);
            onInputChange?.(event, '');
          }
        }}
        multiple={isMultiple}
        noOptionsText='No options'
        disabled={disabled}
        sx={isMultiple ? { '& .MuiInputBase-root': { alignItems: 'center' } } : undefined}
        {...(isMultiple ? { value: value ?? [] } : { defaultValue: defaultValue ?? '' })}
        disableCloseOnSelect
        key={`typeahead-filter-${label}`}
        onChange={(e, newValue: any, reason: any) => {
          e.preventDefault();
          e.stopPropagation();

          if (reason === 'removeOption' && hideCheckboxes) {
            // prevent deletion with backey
            return;
          }

          if (isMultiple && persistSearchOnSelect && reason === 'clear') {
            // Native × clears only search text (handled by onInputChange); leave selections intact
            return;
          }

          if (isMultiple && newValue.length === 0) {
            onChange(undefined, reason);
          } else {
            onChange(newValue, reason);
          }

          if (!isMultiple || hideCheckboxes) {
            setOpen(false);
          }
        }}
        getOptionLabel={
          freeSolo
            ? undefined // this for some reason is causing 'enter' to clear which we do not want in freeSolo
            : (option: { label: string; value: string } | string) => {
                if (typeof option === 'string') {
                  const selectedValue = options?.find((x) => x.value === option);

                  if (selectedItemDefaultLabel && !selectedValue?.value) {
                    return selectedItemDefaultLabel ?? '';
                  }

                  return selectedValue?.label ?? '';
                }
                return option.label;
              }
        }
        renderOption={
          isMultiple && !hideCheckboxes
            ? (props, option: any, { selected }) => {
                const { key, ...optionProps } = props;

                return (
                  <MenuItem
                    key={`${key}-${option.value}`}
                    {...optionProps}
                    sx={{
                      wordBreak: 'break-all',
                      wordWrap: 'break-all',
                      whiteSpace: 'normal',
                      height: 'auto',
                      ...option.sx,
                    }}
                  >
                    <Box sx={{ height: '100%', alignItems: 'flex-start', display: 'flex' }}>
                      <Checkbox
                        sxProps={{
                          mt: option.subLabel && !horizontalLabel ? 0.3 : 'inherit',
                          height: 0,
                          background: 'transparent',
                        }}
                        checked={selected}
                      />

                      {horizontalLabel ? (
                        <Typography variant='bodySmall' display='block'>
                          {option.label}{' '}
                          {option.subLabel && (
                            <Typography variant='caption' color='text.secondary'>
                              ({option.subLabel})
                            </Typography>
                          )}
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'block' }}>
                          <Typography variant='bodySmall' display='block' sx={option.sx}>
                            {option.label}{' '}
                            {option.icon ? (
                              <SvgIcon sx={{ position: 'relative', top: 3, fontSize: 14, color: 'text.subdued' }}>
                                {option.icon}
                              </SvgIcon>
                            ) : null}
                          </Typography>

                          {option.subLabel && (
                            <Typography variant='caption' color='text.secondary'>
                              {option.subLabel}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  </MenuItem>
                );
              }
            : (props, option: any) => {
                const { key, ...optionProps } = props;

                //  get the text before and after the search text
                if (highlightSearchText && inputValue) {
                  const label = option.label;
                  const searchTextIndex = label.toLowerCase().indexOf(inputValue.toLowerCase());
                  const beforeText = label.slice(0, searchTextIndex);
                  const afterText = label.slice(searchTextIndex + inputValue.length);
                  const inBetweenText = label.slice(searchTextIndex, searchTextIndex + inputValue.length);

                  return (
                    <MenuItem
                      key={`${key}-${option.value}`}
                      {...optionProps}
                      sx={{
                        wordBreak: 'break-all',
                        wordWrap: 'break-all',
                        whiteSpace: 'normal',
                        height: 'auto',
                        ...option.sx,
                      }}
                    >
                      <Box sx={{ height: '100%', display: 'flex', alignItems: 'flex-start' }}>
                        <Typography variant='bodySmall' display='block'>
                          {beforeText}
                          <Box component='span' style={{ fontWeight: 'bold' }}>
                            {inBetweenText}
                          </Box>
                          {afterText}
                          {option.subLabel && (
                            <Typography variant='caption' color='text.secondary'>
                              ({option.subLabel})
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    </MenuItem>
                  );
                }
                return (
                  <MenuItem
                    key={`${key}-${option.value}`}
                    {...optionProps}
                    sx={{
                      wordBreak: 'break-all',
                      wordWrap: 'break-all',
                      whiteSpace: 'normal',
                      height: 'auto',
                    }}
                  >
                    {horizontalLabel ? (
                      <Typography variant='bodySmall' display='block'>
                        {option.label}{' '}
                        {option.subLabel && (
                          <Typography variant='caption' color='text.secondary'>
                            ({option.subLabel})
                          </Typography>
                        )}
                      </Typography>
                    ) : (
                      <Box sx={{ height: '100%', display: 'flex', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'block' }}>
                          <Typography variant='bodySmall' display='block'>
                            {option.label}
                          </Typography>

                          {option.subLabel && (
                            <Typography variant='caption' color='text.secondary'>
                              {option.subLabel}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </MenuItem>
                );
              }
        }
        renderValue={
          !isMultiple
            ? undefined
            : () => {
                if (hideTags) return null;
                const list = Array.isArray(value) ? value : [value];

                if (list.length === 0) {
                  return null;
                }

                let displayList = list
                  .slice(0, limitTags)
                  .map((item: any) => item?.label)
                  .join(', ');

                const firstSelectedLabel = displayList.length > 20 ? displayList.substring(0, 20) + '...' : displayList;

                if (list.length > limitTags) {
                  displayList = `${firstSelectedLabel} +${list.length - limitTags}`;
                  return (
                    <Tooltip title={list.map((item: any) => item?.label).join(', ')}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                        <Chip variant='outlined' disabled={disabled} label={displayList} sxProps={chipSxProps} />
                      </Box>
                    </Tooltip>
                  );
                }

                if (firstSelectedLabel.length !== displayList.length) {
                  return (
                    <Tooltip title={list.map((item: any) => item?.label).join(', ')}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                        <Chip
                          variant='outlined'
                          label={firstSelectedLabel}
                          sxProps={chipSxProps}
                          onDelete={() => {
                            onChange(isMultiple ? [] : undefined);
                            setOpen(false);
                          }}
                        />
                      </Box>
                    </Tooltip>
                  );
                }

                return (
                  <Chip
                    variant='outlined'
                    label={firstSelectedLabel}
                    sxProps={chipSxProps}
                    onDelete={() => {
                      onChange(isMultiple ? [] : undefined);
                      setOpen(false);
                    }}
                  />
                );
              }
        }
        renderInput={(params: any) => (
          <TextField {...params} inputLabel={label ?? ''} error={error} helperText={helperText} required={required} />
        )}
        options={options}
        fullWidth
        isOptionEqualToValue={(option: any, value: any) => {
          return option?.value === value?.value;
        }}
        loading={loading && !freeSolo}
        inputValue={inputValue ?? ''}
        slotProps={slotProps}
        onInputChange={(event, value, reason) => {
          if (hideCheckboxes && inputValue && !value && reason === 'reset') {
            return;
          }

          if (isMultiple && persistSearchOnSelect && reason !== 'input' && reason !== 'clear') {
            return;
          }

          setInputValue(value);
          onInputChange?.(event, value);
        }}
      />
    </ClickAwayListener>
  );
};
